import { AsyncPipe, NgTemplateOutlet } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, OnInit } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { untilDestroyed } from '@ngneat/until-destroy';
import { concatLatestFrom } from '@ngrx/operators';
import {
  BehaviorSubject,
  catchError,
  concatMap,
  distinctUntilChanged,
  map,
  Observable,
  of,
  take,
} from 'rxjs';

import { GoogleApiService } from '@blaze-shared/connectors-data-access';
import { ConnectorConfiguration, GoogleServiceType } from '@blaze-shared/connectors-domain';
import {
  CopyConnectorConfigEnvButtonComponent,
  DialogModule,
  EnvironmentIndicatorComponent,
  SelectComponent,
} from '@blaze-shared/shared-feature';
import { ButtonBarComponent, ButtonComponent, PanelComponent } from '@blaze-shared/shared-ui';
import { mapObjectsToOptions, SelectOption } from '@blaze-shared/shared-util';
import { IconsModule } from '@blaze-shared/theming-data-access';

import { BaseConnectorDialogComponent } from '../../__base-connector-dialog/base-connector-dialog.component';
import { prepareConnectorConfiguration } from '../../utils';

import {
  ConnectorForm,
  GoogleCalendarConfiguration,
} from './google-calendar-connector-dialog.models';

@Component({
  selector: 'blaze-google-calendar-connector-dialog',
  imports: [
    AsyncPipe,
    ButtonBarComponent,
    ButtonComponent,
    CopyConnectorConfigEnvButtonComponent,
    DialogModule,
    EnvironmentIndicatorComponent,
    IconsModule,
    NgTemplateOutlet,
    ReactiveFormsModule,
    SelectComponent,
    PanelComponent,
  ],
  templateUrl: './google-calendar-connector-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'data-connector-dialog' },
})
export class GoogleCalendarConnectorDialogComponent
  extends BaseConnectorDialogComponent<GoogleCalendarConfiguration>
  implements OnInit
{
  private googleApiService = inject(GoogleApiService);

  private availableCalendars$$ = new BehaviorSubject<SelectOption<string>[]>([]);

  serviceName = 'google';

  availableCalendars$ = this.availableCalendars$$.asObservable();
  connectorForm: FormGroup<ConnectorForm>;

  constructor() {
    super();
    this.connectorForm = new FormGroup({
      connectorName: new FormControl('', { nonNullable: true, validators: Validators.required }),
      calendarId: new FormControl('', { nonNullable: true, validators: Validators.required }),
    });

    this.setupPhase$
      .pipe(
        untilDestroyed(this),
        distinctUntilChanged(),
        concatMap(() => this.loadCalendars(this.authState)),
      )
      .subscribe((options) => {
        this.availableCalendars$$.next(options);
      });

    this.availableCalendars$
      .pipe(
        untilDestroyed(this),
        concatLatestFrom(() => this.connectorForm.controls.calendarId.valueChanges),
      )
      .subscribe(([options, selectedCalendarId]) => {
        if (options.some((option) => option.value === selectedCalendarId)) {
          return;
        }
        this.connectorForm.controls.calendarId.setValue('', { emitEvent: false });
      });

    this.withOAuth();
  }

  ngOnInit(): void {
    const configuration = this.getConfiguration(this.connector);
    this.fillForm(configuration);
    this.goToAuth({ reauthorize: true });
  }

  getConfiguration(connector?: ConnectorConfiguration | undefined): GoogleCalendarConfiguration {
    const connectorId = connector?.id ?? '';
    const connectorName = connector?.name ?? '';
    const authState = this.authState ?? String(connector?.configuration?.authState ?? '');
    const calendarId = String(connector?.configuration?.calendarId ?? '');

    return { connectorId, connectorName, authState, calendarId };
  }

  fillForm(configuration: GoogleCalendarConfiguration): void {
    const { connectorId, connectorName, authState, calendarId } = configuration;

    this.connectorId = connectorId;
    this.authState = authState;

    this.loadingDialog$$.next(true);
    this.loadCalendars(authState)
      .pipe(untilDestroyed(this), take(1))
      .subscribe((options) => {
        this.connectorForm.setValue(
          {
            connectorName,
            calendarId,
          },
          { emitEvent: false },
        );

        this.availableCalendars$$.next(options);
        this.loadingDialog$$.next(false);
      });
  }

  loadCalendars(authState: string): Observable<SelectOption<string>[]> {
    if (!authState || !this.appInstalled()) {
      return of([]);
    }

    return this.googleApiService.getCalendars(authState).pipe(
      catchError((error) => {
        this.handleErrors(error);
        return of([]);
      }),
      map((calendars) => mapObjectsToOptions(calendars, 'id', 'summary') ?? []),
    );
  }

  saveConnector(): void {
    if (this.connectorForm.invalid) {
      return;
    }

    const { connectorName, calendarId } = this.connectorForm.value;

    if (!connectorName || !calendarId) {
      return;
    }

    const connectorConfig = prepareConnectorConfiguration(
      connectorName,
      this.template?.id,
      this.accountId,
      this.connector,
      {
        authState: this.authState,
        calendarId,
      },
    );

    this.saveOrUpdateConnector(connectorConfig, this.connectorId)
      .pipe(untilDestroyed(this))
      .subscribe((connector) => {
        if (connector) {
          this.showDatasets.emit(connector);
        }
      });
  }

  protected override getAuthorizationUrl(): Observable<{ url: string }> {
    return this.connectorsApiService.getServiceAuthorizeUrl(this.serviceName, {
      googleServiceType: GoogleServiceType.Calendar,
    });
  }
}
